import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  signal,
  WritableSignal,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
  NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SettingsService } from '@app/_services/settings.service';
import { CacheService } from '@app/_services/cache.service';

/**
 * Interface for day data matching entity component's weekDays structure
 */
export interface WeekDay {
  name: string;
  narrowName: string;
  date: number;
  dateObject: Date;
  active: boolean;
  isToday: boolean;
  hasData: boolean;
}

/**
 * Swipe direction enum for gesture detection
 */
type SwipeDirection = 'left' | 'right' | 'down' | 'up' | 'none';

/**
 * MobileWeeklyCalendarComponent
 *
 * A mobile-optimized weekly calendar component following Flutter's implementation:
 * - Horizontal row of 7 days with tap-to-select
 * - Swipe LEFT/RIGHT for week navigation
 * - Swipe DOWN to open month calendar
 * - Lazy loading of data dots (staggered 300ms + index * 50ms)
 * - Direction-aware slide animations
 *
 * Based on Flutter's WeeklyCalendarWidgetMobileUI and SelfUpgradingDateWidget
 */
@Component({
  selector: 'app-mobile-weekly-calendar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mobile-weekly-calendar.component.html',
  styleUrl: './mobile-weekly-calendar.component.scss'
})
export class MobileWeeklyCalendarComponent implements OnInit, OnDestroy, OnChanges {
  readonly ss = inject(SettingsService);
  private readonly cc = inject(CacheService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly elementRef = inject(ElementRef);

  // Input: Week days array from parent component
  @Input() weekDays: WeekDay[] = [];

  // Outputs for parent component interaction
  @Output() daySelected = new EventEmitter<WeekDay>();
  @Output() navigateWeek = new EventEmitter<'next' | 'prev'>();
  @Output() openMonthCalendar = new EventEmitter<void>();

  // Gesture detection state
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private isSwiping = false;

  // Gesture thresholds matching Flutter's SwipeDetector
  private readonly SWIPE_DISTANCE_THRESHOLD = 50; // pixels
  private readonly SWIPE_VELOCITY_THRESHOLD = 300; // pixels/second

  // Lazy loading state for dots
  // Each day gets upgraded after a staggered delay: 300ms + (index * 50ms)
  dotsLoaded: WritableSignal<boolean[]> = signal([false, false, false, false, false, false, false]);
  private dotLoadTimers: ReturnType<typeof setTimeout>[] = [];

  // Animation state
  slideDirection: WritableSignal<'slide-left' | 'slide-right' | ''> = signal('');
  private readonly ANIMATION_DURATION = 300; // ms, matching Flutter

  ngOnInit(): void {
    // Start lazy loading dots after initial render
    this.scheduleDotLoading();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When weekDays input changes and any day now has data that didn't before,
    // reschedule dot loading to show the fade-in animation
    if (changes['weekDays'] && !changes['weekDays'].firstChange) {
      const prevWeekDays = changes['weekDays'].previousValue || [];
      const currWeekDays = changes['weekDays'].currentValue || [];

      // Check if any day gained hasData (false -> true)
      const hasNewData = currWeekDays.some((day: WeekDay, index: number) => {
        const prevDay = prevWeekDays[index];
        return day.hasData && (!prevDay || !prevDay.hasData);
      });

      if (hasNewData) {
        // Reschedule dot loading for smooth animation of newly available dots
        this.scheduleDotLoading();
      }
    }
  }

  ngOnDestroy(): void {
    // Clear all pending timers
    this.dotLoadTimers.forEach(timer => clearTimeout(timer));
    this.dotLoadTimers = [];
  }

  /**
   * Schedule staggered dot loading following Flutter's pattern:
   * - Initial delay: 300ms
   * - Between-date delay: 50ms per index
   *
   * This ensures fast initial render, then progressively loads data indicators
   */
  private scheduleDotLoading(): void {
    // Clear existing timers
    this.dotLoadTimers.forEach(timer => clearTimeout(timer));
    this.dotLoadTimers = [];

    // Reset dots loaded state
    this.dotsLoaded.set([false, false, false, false, false, false, false]);

    const INITIAL_DELAY = 300; // ms
    const BETWEEN_DELAY = 50; // ms per date

    for (let i = 0; i < 7; i++) {
      const delay = INITIAL_DELAY + (i * BETWEEN_DELAY);
      const timer = setTimeout(() => {
        this.ngZone.run(() => {
          const current = this.dotsLoaded();
          current[i] = true;
          this.dotsLoaded.set([...current]);
          this.cdr.markForCheck();
        });
      }, delay);
      this.dotLoadTimers.push(timer);
    }
  }

  /**
   * Handle day tap - emit selection event to parent
   */
  onDayTap(day: WeekDay, event: Event): void {
    event.stopPropagation();
    this.daySelected.emit(day);
  }

  /**
   * Handle previous week arrow click
   * Navigates to same day of week, 7 days earlier
   */
  onPreviousWeek(event: Event): void {
    event.stopPropagation();
    this.animateAndNavigate('prev');
  }

  /**
   * Handle next week arrow click
   * Navigates to same day of week, 7 days later
   */
  onNextWeek(event: Event): void {
    event.stopPropagation();
    this.animateAndNavigate('next');
  }

  /**
   * Touch event handlers for swipe gesture detection
   */
  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
    this.isSwiping = true;
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    // Prevent default scrolling during horizontal swipe
    if (!this.isSwiping || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    // If horizontal movement is greater than vertical, prevent vertical scroll
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      event.preventDefault();
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (!this.isSwiping) return;
    this.isSwiping = false;

    const touch = event.changedTouches[0];
    const endX = touch.clientX;
    const endY = touch.clientY;
    const endTime = Date.now();

    this.handleSwipe(endX, endY, endTime);
  }

  @HostListener('touchcancel')
  onTouchCancel(): void {
    this.isSwiping = false;
  }

  /**
   * Process swipe gesture and determine direction
   * Follows Flutter's SwipeDetector logic with distance and velocity thresholds
   */
  private handleSwipe(endX: number, endY: number, endTime: number): void {
    const deltaX = endX - this.touchStartX;
    const deltaY = endY - this.touchStartY;
    const duration = endTime - this.touchStartTime;

    // Calculate distance and velocity
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const velocity = (distance / duration) * 1000; // pixels per second

    // Check if swipe meets thresholds
    if (distance < this.SWIPE_DISTANCE_THRESHOLD || velocity < this.SWIPE_VELOCITY_THRESHOLD) {
      return; // Not a valid swipe
    }

    // Determine direction based on which axis has greater movement
    const direction = this.getSwipeDirection(deltaX, deltaY);

    const isRtl = this.ss.appDirection() === 'rtl';
    switch (direction) {
      case 'left':
        // Physical left swipe: next in LTR, prev in RTL
        this.animateAndNavigate(isRtl ? 'prev' : 'next');
        break;
      case 'right':
        // Physical right swipe: prev in LTR, next in RTL
        this.animateAndNavigate(isRtl ? 'next' : 'prev');
        break;
      case 'down':
        // Swipe down -> open month calendar
        this.openMonthCalendar.emit();
        break;
      // 'up' swipe is not handled in this component
    }
  }

  /**
   * Determine swipe direction from delta values
   */
  private getSwipeDirection(deltaX: number, deltaY: number): SwipeDirection {
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // Horizontal swipe has priority if X movement is greater
    if (absX > absY) {
      return deltaX < 0 ? 'left' : 'right';
    } else {
      return deltaY > 0 ? 'down' : 'up';
    }
  }

  /**
   * Animate the slide transition and emit navigation event
   */
  private animateAndNavigate(direction: 'next' | 'prev'): void {
    // Set slide animation direction
    // 'next' (swipe left) = content slides in from right (slide-left class)
    // 'prev' (swipe right) = content slides in from left (slide-right class)
    this.slideDirection.set(direction === 'next' ? 'slide-left' : 'slide-right');

    // Emit navigation event to parent
    this.navigateWeek.emit(direction);

    // Reset dot loading for new week
    this.scheduleDotLoading();

    // Clear animation after transition completes
    setTimeout(() => {
      this.slideDirection.set('');
      this.cdr.markForCheck();
    }, this.ANIMATION_DURATION);
  }

  /**
   * Check if a day's dot should be visible
   * Only shows dot if: day has data AND lazy loading for that index is complete
   */
  shouldShowDot(index: number, day: WeekDay): boolean {
    return this.dotsLoaded()[index] && day.hasData;
  }

  /**
   * Get the theme from settings service
   */
  get theme() {
    return this.ss.theme();
  }

  /**
   * Track by function for ngFor performance.
   * Uses only date components for stable identity - Angular's change detection
   * handles hasData property updates naturally.
   */
  trackByDay(index: number, day: WeekDay): string {
    return `${day.dateObject.getFullYear()}-${day.dateObject.getMonth()}-${day.dateObject.getDate()}`;
  }
}
